import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { userApi, tagApi } from '../utils/api';
import ChessDiagramGenerator from './ChessDiagramGenerator';

const DEFAULT_PREFS = {
  theme: 'light',
  cardsPerPage: 12,
  showFen: true,
  // Diagram defaults — mirrors ChessDiagramGenerator's own defaults
  diagramPieceSet:      'cburnett',
  diagramLightSquare:   '#ffffdd',
  diagramDarkSquare:    '#8ba56c',
  diagramBorderColor:   '#000000',
  diagramCoordPosition: 'border',
  diagramFlipped:             false,
  diagramShowSideToMoveDisc:  false,
};

export default function PreferencesPage() {
  const { token, user, prefs: ctxPrefs, refreshUser, refreshPrefs } = useAuth();

  // ── Profile state ──────────────────────────────────────────────────────────
  const [profile, setProfile]           = useState({ first_name: '', last_name: '', nickname: '', email_address: '' });
  const [profileFieldErrors, setProfileFieldErrors] = useState({ first_name: '', last_name: '', nickname: '', email_address: '' });
  const [profileMsg, setProfileMsg]     = useState('');
  const [profileErr, setProfileErr]     = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const PROFILE_LABELS = { first_name: 'First name', last_name: 'Last name', nickname: 'Nickname', email_address: 'Email address' };

  // ── General preferences state ──────────────────────────────────────────────
  const [prefs, setPrefs]         = useState(() => ({ ...DEFAULT_PREFS, ...(ctxPrefs || {}) }));
  const [prefsMsg, setPrefsMsg]   = useState('');
  const [prefsErr, setPrefsErr]   = useState('');
  const [savingPrefs, setSavingPrefs] = useState(false);

  // ── Tags state ─────────────────────────────────────────────────────────────
  const [userTags, setUserTags]           = useState([]);
  const [tagInput, setTagInput]           = useState('');
  const [editingTagId, setEditingTagId]   = useState(null);
  const [editingTagVal, setEditingTagVal] = useState('');
  const [tagsMsg, setTagsMsg]             = useState('');
  const [tagsErr, setTagsErr]             = useState('');

  // ── Diagram preferences — tracked live via ChessDiagramGenerator callback ─
  // We store them in a ref so the callback doesn't cause re-renders on every
  // slider/color change; the value is flushed into `prefs` only on Save.
  const pendingDiagramPrefs = useRef({});
  const [diagMsg, setDiagMsg] = useState('');
  const [diagErr, setDiagErr] = useState('');
  const [savingDiag, setSavingDiag] = useState(false);

  // ── Load tags on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    if (token) {
      tagApi.list(token).then(setUserTags).catch(() => {});
    }
  }, [token]);

  // ── Load data on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    if (user) {
      setProfile(p => ({
        ...p,
        first_name:    user.first_name    || '',
        last_name:     user.last_name     || '',
        nickname:      user.nickname      || '',
        email_address: user.email_address || '',
      }));
    }
  }, [user]);

  // Seed from context (already loaded at login — no extra network call needed)
  useEffect(() => {
    if (ctxPrefs) setPrefs({ ...DEFAULT_PREFS, ...ctxPrefs });
  }, [ctxPrefs]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleProfile = e => {
    const { name, value } = e.target;
    setProfile(p => ({ ...p, [name]: value }));
    // Clear the field error as soon as the user starts correcting it
    if (name in profileFieldErrors && profileFieldErrors[name]) {
      setProfileFieldErrors(fe => ({ ...fe, [name]: '' }));
    }
  };

  const handleProfileBlur = e => {
    const { name, value } = e.target;
    if (name in PROFILE_LABELS && !value.trim()) {
      setProfileFieldErrors(fe => ({ ...fe, [name]: `${PROFILE_LABELS[name]} is required.` }));
    }
  };
  const handlePrefs   = e => {
    const { name, value, type, checked } = e.target;
    setPrefs(p => ({ ...p, [name]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value) }));
  };

  // ChessDiagramGenerator fires this on every change; we just stash it
  const handleDiagramPrefsChange = useCallback((dp) => {
    pendingDiagramPrefs.current = dp;
  }, []);

  // ── Tag handlers ───────────────────────────────────────────────────────────

  const addTag = async () => {
    const name = tagInput.trim();
    if (!name) return;
    setTagsErr(''); setTagsMsg('');
    try {
      const created = await tagApi.create(name, token);
      setUserTags(ts => [...ts, created].sort((a, b) => a.tag.localeCompare(b.tag)));
      setTagInput('');
      setTagsMsg('Tag added.');
    } catch (err) { setTagsErr(err.message); }
  };

  const saveTagEdit = async (id) => {
    const name = editingTagVal.trim();
    if (!name) return;
    setTagsErr(''); setTagsMsg('');
    try {
      await tagApi.update(id, name, token);
      setUserTags(ts => ts.map(t => t.id === id ? { ...t, tag: name } : t)
                          .sort((a, b) => a.tag.localeCompare(b.tag)));
      setEditingTagId(null);
      setTagsMsg('Tag saved.');
    } catch (err) { setTagsErr(err.message); }
  };

  const deleteTag = async (id) => {
    if (!window.confirm('Delete this tag? It will be removed from all flashcards.')) return;
    setTagsErr(''); setTagsMsg('');
    try {
      await tagApi.delete(id, token);
      setUserTags(ts => ts.filter(t => t.id !== id));
    } catch (err) { setTagsErr(err.message); }
  };

  const saveProfile = async e => {
    e.preventDefault();
    setProfileErr(''); setProfileMsg('');

    // Validate trimmed values for required fields
    const fieldErrors = {
      first_name:    profile.first_name.trim()    ? '' : 'First name is required.',
      last_name:     profile.last_name.trim()     ? '' : 'Last name is required.',
      nickname:      profile.nickname.trim()      ? '' : 'Nickname is required.',
      email_address: profile.email_address.trim() ? '' : 'Email address is required.',
    };
    if (Object.values(fieldErrors).some(Boolean)) {
      setProfileFieldErrors(fieldErrors);
      return;
    }

    setSavingProfile(true);
    try {
      const payload = {
        first_name:    profile.first_name.trim(),
        last_name:     profile.last_name.trim(),
        nickname:      profile.nickname.trim(),
        email_address: profile.email_address.trim(),
      };

      await userApi.updateMe(payload, token);
      await refreshUser();
      setProfile(p => ({ ...p,
        first_name:    p.first_name.trim(),
        last_name:     p.last_name.trim(),
        nickname:      p.nickname.trim(),
        email_address: p.email_address.trim(),
      }));
      setProfileMsg('Profile saved!');
    } catch (err) {
      setProfileErr(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePrefs = async e => {
    e.preventDefault();
    setPrefsErr(''); setPrefsMsg('');
    setSavingPrefs(true);
    try {
      await userApi.savePreferences(prefs, token);
      await refreshPrefs();
      setPrefsMsg('Preferences saved!');
    } catch (err) {
      setPrefsErr(err.message);
    } finally {
      setSavingPrefs(false);
    }
  };

  // Save current diagram settings (from the generator's live state) to backend
  const saveDiagramPrefs = async () => {
    setDiagErr(''); setDiagMsg('');
    setSavingDiag(true);
    try {
      // Merge diagram prefs into the full preferences object
      const merged = { ...prefs, ...pendingDiagramPrefs.current };
      await userApi.savePreferences(merged, token);
      await refreshPrefs();          // update AuthContext so all pages see new values immediately
      setDiagMsg('Diagram preferences saved!');
    } catch (err) {
      setDiagErr(err.message);
    } finally {
      setSavingDiag(false);
    }
  };

  return (
    <div>
      <h2 className="mb-4">Account &amp; Preferences</h2>

        {/* ── Profile ──────────────────────────────────────────────────── */}
        <div className="card shadow-sm mb-4">
          <div className="card-header"><h5 className="mb-0">Profile</h5></div>
          <div className="card-body">
            {profileMsg && <div className="alert alert-success py-2">{profileMsg}</div>}
            {profileErr && <div className="alert alert-danger  py-2">{profileErr}</div>}
            <form onSubmit={saveProfile}>
              <div className="row mb-3">
                <div className="col">
                  <label className="form-label">First name <span className="text-danger">*</span></label>
                  <input
                    type="text" name="first_name"
                    className={`form-control${profileFieldErrors.first_name ? ' is-invalid' : ''}`}
                    value={profile.first_name}
                    onChange={handleProfile}
                    onBlur={handleProfileBlur}
                  />
                  {profileFieldErrors.first_name && (
                    <div className="invalid-feedback">{profileFieldErrors.first_name}</div>
                  )}
                </div>
                <div className="col">
                  <label className="form-label">Last name <span className="text-danger">*</span></label>
                  <input
                    type="text" name="last_name"
                    className={`form-control${profileFieldErrors.last_name ? ' is-invalid' : ''}`}
                    value={profile.last_name}
                    onChange={handleProfile}
                    onBlur={handleProfileBlur}
                  />
                  {profileFieldErrors.last_name && (
                    <div className="invalid-feedback">{profileFieldErrors.last_name}</div>
                  )}
                </div>
                <div className="col">
                  <label className="form-label">Nickname <span className="text-danger">*</span></label>
                  <input
                      type="text" name="nickname"
                      className={`form-control${profileFieldErrors.nickname ? ' is-invalid' : ''}`}
                      value={profile.nickname}
                      onChange={handleProfile}
                      onBlur={handleProfileBlur}
                  />
                  {profileFieldErrors.nickname && (
                      <div className="invalid-feedback">{profileFieldErrors.nickname}</div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Email address <span className="text-danger">*</span></label>
                <input
                  type="email" name="email_address"
                  className={`form-control${profileFieldErrors.email_address ? ' is-invalid' : ''}`}
                  value={profile.email_address}
                  onChange={handleProfile}
                  onBlur={handleProfileBlur}
                />
                {profileFieldErrors.email_address && (
                  <div className="invalid-feedback">{profileFieldErrors.email_address}</div>
                )}
              </div>
              {/*<hr />*/}
              <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                {savingProfile ? 'Saving…' : 'Save Profile'}
              </button>
            </form>
          </div>
        </div>

        {/* ── General Preferences ──────────────────────────────────────── */}
        <div className="card shadow-sm mb-4">
          <div className="card-header"><h5 className="mb-0">General Preferences</h5></div>
          <div className="card-body">
            {prefsMsg && <div className="alert alert-success py-2">{prefsMsg}</div>}
            {prefsErr && <div className="alert alert-danger  py-2">{prefsErr}</div>}
            <form onSubmit={savePrefs}>
              <div className="mb-3">
                <label className="form-label">Theme</label>
                <select name="theme" className="form-select" value={prefs.theme} onChange={handlePrefs}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
              <div className="mb-3 form-check">
                <input type="checkbox" className="form-check-input" id="showFen"
                  name="showFen" checked={prefs.showFen} onChange={handlePrefs} />
                <label className="form-check-label" htmlFor="showFen">Show FEN string on cards</label>
              </div>
              <div className="mb-3 form-check">
                <input type="checkbox" className="form-check-input" id="showTagsOnView"
                  name="showTagsOnView" checked={prefs.showTagsOnView ?? true} onChange={handlePrefs} />
                <label className="form-check-label" htmlFor="showTagsOnView">Show Tags when viewing a Flashcard</label>
              </div>
              <div className="mb-3 form-check">
                <input type="checkbox" className="form-check-input" id="showTagsInQuiz"
                  name="showTagsInQuiz" checked={prefs.showTagsInQuiz ?? false} onChange={handlePrefs} />
                <label className="form-check-label" htmlFor="showTagsInQuiz">Show Tags in Quiz Mode</label>
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingPrefs}>
                {savingPrefs ? 'Saving…' : 'Save Preferences'}
              </button>
            </form>
          </div>
        </div>

        {/* ── Tags ─────────────────────────────────────────────────────── */}
        <div className="card shadow-sm mb-4">
          <div className="card-header"><h5 className="mb-0">Tags</h5></div>
          <div className="card-body">
            {tagsMsg && <div className="alert alert-success py-2">{tagsMsg}</div>}
            {tagsErr && <div className="alert alert-danger  py-2">{tagsErr}</div>}

            {/* Existing tags */}
            {userTags.length > 0 && (
              <ul className="list-group mb-3">
                {userTags.map(t => (
                  <li key={t.id} className="list-group-item d-flex align-items-center gap-2 py-2">
                    {editingTagId === t.id ? (
                      <>
                        <input
                          className="form-control form-control-sm"
                          style={{ maxWidth: 220 }}
                          value={editingTagVal}
                          onChange={e => setEditingTagVal(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveTagEdit(t.id); if (e.key === 'Escape') setEditingTagId(null); }}
                          autoFocus
                        />
                        <button className="btn btn-sm btn-primary" onClick={() => saveTagEdit(t.id)}>Save</button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingTagId(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <span className="flex-grow-1">{t.tag}</span>
                        <button className="btn btn-sm btn-outline-secondary"
                          onClick={() => { setEditingTagId(t.id); setEditingTagVal(t.tag); }}>
                          Rename
                        </button>
                        <button className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteTag(t.id)}>
                          Delete
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Add new tag */}
            <div className="d-flex gap-2" style={{ maxWidth: 340 }}>
              <input
                className="form-control form-control-sm"
                placeholder="New tag name…"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              />
              <button className="btn btn-sm btn-success" onClick={addTag} disabled={!tagInput.trim()}>
                Add
              </button>
            </div>
          </div>
        </div>

        {/* ── Diagram Preferences ──────────────────────────────────────── */}
        <div className="card shadow-sm mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Diagram Preferences</h5>
            <small className="text-muted">Adjust the board, then save as your defaults</small>
          </div>
          <div className="card-body p-0">
            {diagMsg && <div className="alert alert-success py-2 mx-3 mt-3 mb-0">{diagMsg}</div>}
            {diagErr && <div className="alert alert-danger  py-2 mx-3 mt-3 mb-0">{diagErr}</div>}

            {/* Full generator — seeded with saved prefs, fires callback on change */}
            <ChessDiagramGenerator
              initialPrefs={prefs}
              onPreferencesChange={handleDiagramPrefsChange}
              boardSize={320}
            />

            <div className="p-3 border-top">
              <button
                className="btn btn-primary"
                onClick={saveDiagramPrefs}
                disabled={savingDiag}
              >
                {savingDiag ? 'Saving…' : 'Save as Default Diagram Style'}
              </button>
              <span className="ms-3 text-muted small">
                These settings will be applied when viewing any flashcard with a FEN string.
              </span>
            </div>
          </div>
        </div>

    </div>
  );
}
